/**
 * Outcome Metrics — the four numbers a $1B home-improvement operator scores
 * scheduling against. Replaces activity tiles ("installs today: 3") with the
 * KPIs that actually move margin per crew-day.
 *
 *   • Crew Utilization %   — booked install-days ÷ available install-days (90d trailing)
 *   • First-Time-Complete % — wraps with outcome="completed" ÷ total wraps
 *   • Travel Share %       — naive proxy: % of jobs whose city differs from the crew's
 *                            most-frequent city this period (windshield leak indicator)
 *   • Reschedule Rate %    — jobs whose scheduledInstallDate moved at least once
 *                            (detected via activityHistory) ÷ scheduled jobs
 *
 * Pure data: no React, no UI imports. Composes types only.
 */
import { startOfDay, subDays, parseISO, isValid } from "date-fns";
import type { Job } from "@/data/types";
import type { InstallerWrapRecord } from "@/data/installerWrapEngine";

export interface OutcomeMetric {
  key: "utilization" | "first_time_complete" | "travel_share" | "reschedule";
  label: string;
  value: number | null;     // percentage 0-100, or null if insufficient data
  /** Healthy/warn/bad band — operator-readable signal. */
  tone: "good" | "warn" | "bad" | "muted";
  /** Sub-line (e.g. "of 47 scheduled days"). */
  sub: string;
  /** Higher-is-better? Used to flip tone direction. */
  higherIsBetter: boolean;
  /** Target the metric should beat to read as "good". */
  target: number;
}

interface ComputeOpts {
  jobs: Job[];
  wraps: InstallerWrapRecord[];
  /** Window in days (default 90). */
  windowDays?: number;
  referenceDate?: Date;
}

export function computeOutcomeMetrics(opts: ComputeOpts): OutcomeMetric[] {
  const { jobs, wraps, windowDays = 90, referenceDate = new Date() } = opts;
  const windowStart = startOfDay(subDays(referenceDate, windowDays));

  // ── Utilization ──
  // Booked install-days = unique (crew, date) pairs scheduled in window.
  // Available days = active crews × (5 days/wk × ⌈window/7⌉ weeks).
  const crewSet = new Set<string>();
  const bookedPairs = new Set<string>();
  for (const j of jobs) {
    const crew = j.assignedCrew || j.assignedSubcontractor;
    if (!crew) continue;
    crewSet.add(crew);
    if (!j.scheduledInstallDate) continue;
    const d = parseISO(j.scheduledInstallDate);
    if (!isValid(d) || d < windowStart || d > referenceDate) continue;
    bookedPairs.add(`${crew}|${j.scheduledInstallDate.slice(0, 10)}`);
  }
  const available = Math.max(1, crewSet.size * 5 * Math.ceil(windowDays / 7));
  const utilization = Math.round((bookedPairs.size / available) * 100);

  // ── First-Time-Complete ──
  const recentWraps = wraps.filter(w => {
    const d = parseISO(w.timestamp);
    return isValid(d) && d >= windowStart && d <= referenceDate;
  });
  const completed = recentWraps.filter(w => w.outcome === "completed_today").length;
  const ftc = recentWraps.length > 0
    ? Math.round((completed / recentWraps.length) * 100)
    : null;

  // ── Travel Share (proxy) ──
  // For each crew, find most-frequent city; count jobs in *other* cities as travel-heavy.
  const crewCity = new Map<string, Map<string, number>>();
  const crewTravel = new Map<string, { offHome: number; total: number }>();
  for (const j of jobs) {
    const crew = j.assignedCrew || j.assignedSubcontractor;
    if (!crew || !j.address?.city) continue;
    if (!j.scheduledInstallDate) continue;
    const d = parseISO(j.scheduledInstallDate);
    if (!isValid(d) || d < windowStart || d > referenceDate) continue;
    const cityMap = crewCity.get(crew) || new Map<string, number>();
    cityMap.set(j.address.city, (cityMap.get(j.address.city) || 0) + 1);
    crewCity.set(crew, cityMap);
  }
  // Resolve home city per crew = max count.
  for (const [crew, cities] of crewCity) {
    let home = "", max = 0;
    for (const [city, n] of cities) {
      if (n > max) { max = n; home = city; }
    }
    let off = 0, total = 0;
    for (const [city, n] of cities) {
      total += n;
      if (city !== home) off += n;
    }
    crewTravel.set(crew, { offHome: off, total });
  }
  let travelOff = 0, travelTotal = 0;
  for (const t of crewTravel.values()) { travelOff += t.offHome; travelTotal += t.total; }
  const travelShare = travelTotal > 0
    ? Math.round((travelOff / travelTotal) * 100)
    : null;

  // ── Reschedule Rate ──
  // Heuristic: count jobs whose activityHistory mentions reschedule/rescheduled.
  let scheduled = 0, rescheduled = 0;
  for (const j of jobs) {
    if (!j.scheduledInstallDate) continue;
    const d = parseISO(j.scheduledInstallDate);
    if (!isValid(d) || d < windowStart || d > referenceDate) continue;
    scheduled++;
    const hits = (j.activityHistory || []).some(a =>
      /reschedul/i.test(a.action || "")
    );
    if (hits) rescheduled++;
  }
  const reschedule = scheduled > 0
    ? Math.round((rescheduled / scheduled) * 100)
    : null;

  // ── Compose ──
  const utilizationMetric: OutcomeMetric = {
    key: "utilization",
    label: "Crew Utilization",
    value: utilization,
    sub: `${bookedPairs.size} of ${available} crew-days · 90d`,
    higherIsBetter: true,
    target: 75,
    tone: bandHigher(utilization, 75, 60),
  };
  const ftcMetric: OutcomeMetric = {
    key: "first_time_complete",
    label: "First-Time-Complete",
    value: ftc,
    sub: ftc === null ? "no wraps in window" : `${completed} of ${recentWraps.length} wraps · 90d`,
    higherIsBetter: true,
    target: 90,
    tone: ftc === null ? "muted" : bandHigher(ftc, 90, 75),
  };
  const travelMetric: OutcomeMetric = {
    key: "travel_share",
    label: "Travel Share",
    value: travelShare,
    sub: travelShare === null ? "no scheduled jobs" : `${travelOff} of ${travelTotal} jobs off-home · 90d`,
    higherIsBetter: false,
    target: 20,
    tone: travelShare === null ? "muted" : bandLower(travelShare, 20, 35),
  };
  const reschedMetric: OutcomeMetric = {
    key: "reschedule",
    label: "Reschedule Rate",
    value: reschedule,
    sub: reschedule === null ? "no scheduled jobs" : `${rescheduled} of ${scheduled} jobs moved · 90d`,
    higherIsBetter: false,
    target: 8,
    tone: reschedule === null ? "muted" : bandLower(reschedule, 8, 15),
  };
  return [utilizationMetric, ftcMetric, travelMetric, reschedMetric];
}

function bandHigher(v: number, good: number, warn: number): OutcomeMetric["tone"] {
  if (v >= good) return "good";
  if (v >= warn) return "warn";
  return "bad";
}
function bandLower(v: number, good: number, warn: number): OutcomeMetric["tone"] {
  if (v <= good) return "good";
  if (v <= warn) return "warn";
  return "bad";
}
